import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import {
    ClipboardList,
    CheckSquare,
    Users,
    Map,
    MessageSquare,
    TriangleAlert, // Using TriangleAlert for unsafe observations
    ShieldCheck // Using ShieldCheck for safe observations (if needed in future)
} from 'lucide-react';

import {
    getAreas,
    getObservations,
    getUsers,
    getDashboardActivity,
} from '../../services/observationAPI';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const AdminDashboard = () => {
    const [stats, setStats] = useState({
        totalObservations: 0,
        // Updated state names for observation types
        unsafeActObservations: 0,
        unsafeConditionObservations: 0,
        waitingActions: 0,
        inProgressActions: 0,
        submittedActions: 0,
        completedActions: 0,
        totalUsers: 0,
        totalAreas: 0,
        totalCoaching: 0,
    });

    const [activity, setActivity] = useState({
        recentObservations: [],
        recentUsers: [],
    });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [observationsRes, usersRes, areasRes, activityRes] = await Promise.all([
                    getObservations(),
                    getUsers(),
                    getAreas(),
                    getDashboardActivity(),
                ]);

                // Calculate counts for new observation types
                const unsafeActObservations = observationsRes.observations.filter((obs) => obs.type === 'unsafe act').length;
                const unsafeConditionObservations = observationsRes.observations.filter((obs) => obs.type === 'unsafe condition').length;

                // Calculate action statuses directly from observations
                const waitingActions = observationsRes.observations.filter(obs => obs.status === 'waiting').length;
                const inProgressActions = observationsRes.observations.filter(obs => obs.status === 'in-progress').length;
                const submittedActions = observationsRes.observations.filter(obs => obs.status === 'submitted').length;
                const completedActions = observationsRes.observations.filter(obs => obs.status === 'completed').length;


                setStats({
                    totalObservations: observationsRes.total,
                    // Updated state values
                    unsafeActObservations,
                    unsafeConditionObservations,
                    waitingActions,
                    inProgressActions,
                    submittedActions,
                    completedActions,
                    totalUsers: usersRes.length,
                    totalAreas: areasRes.data.length,
                    totalCoaching: activityRes?.totalCoaching || 0,
                });

                setActivity({
                    recentObservations: activityRes.recentObservations || [],
                    recentUsers: activityRes.recentUsers || [],
                });
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const formatTimeAgo = (date) => {
        const time = new Date(date);
        const now = new Date();
        const diff = Math.floor((now - time) / 1000 / 60); // minutes ago
        if (diff < 60) return `${diff} mins ago`;
        const hours = Math.floor(diff / 60);
        return `${hours} hrs ago`;
    };

    // Updated chart data for observation types
    const observationTypeData = {
        labels: ['Unsafe Act', 'Unsafe Condition'],
        datasets: [
            {
                data: [stats.unsafeActObservations, stats.unsafeConditionObservations],
                backgroundColor: ['#F59E0B', '#EF4444'], // Using yellow for Unsafe Act, red for Unsafe Condition
                borderColor: ['#D97706', '#B91C1C'],
                borderWidth: 1,
            },
        ],
    };

    const actionStatusData = {
        labels: ['Waiting', 'In Progress', 'Submitted', 'Completed'],
        datasets: [
            {
                data: [stats.waitingActions, stats.inProgressActions, stats.submittedActions, stats.completedActions],
                backgroundColor: ['#F59E0B', '#9CA3AF', '#3B82F6', '#10B981'],
                borderColor: ['#D97706', '#6B7280', '#2563EB', '#059669'],
                borderWidth: 1,
            },
        ],
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600">
                    Overview of all safety observations, actions, and team
                    performance.
                </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    icon={<ClipboardList className="text-blue-600" />}
                    label="Total Observations"
                    value={stats.totalObservations}
                    bg="bg-blue-100"
                />
                 {/* Stat cards for Unsafe Act and Unsafe Condition */}
                 <StatCard
                    icon={<TriangleAlert className="text-amber-600" />}
                    label="Unsafe Act Observations"
                    value={stats.unsafeActObservations}
                    bg="bg-amber-100"
                />
                 <StatCard
                    icon={<TriangleAlert className="text-red-600" />}
                    label="Unsafe Condition Observations"
                    value={stats.unsafeConditionObservations}
                    bg="bg-red-100"
                />
                <StatCard
                    icon={<CheckSquare className="text-green-600" />}
                    label="Completed Actions"
                    value={stats.completedActions}
                    bg="bg-green-100"
                />
                <StatCard
                    icon={<Users className="text-purple-600" />} // Changed user icon color
                    label="Total Users"
                    value={stats.totalUsers}
                    bg="bg-purple-100" // Changed user stat card color
                />
                <StatCard
                    icon={<Map className="text-sky-600" />}
                    label="Active Areas"
                    value={stats.totalAreas}
                    bg="bg-sky-100"
                />
                <StatCard
                    icon={<MessageSquare className="text-indigo-600" />} // Changed coaching icon color
                    label="Total Coaching Logs"
                    value={stats.totalCoaching}
                    bg="bg-indigo-100" // Changed coaching stat card color
                />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ChartCard
                    title="Observation Types"
                    chart={<Doughnut data={observationTypeData} />}
                />
                <ChartCard
                    title="Action Status"
                    chart={<Doughnut data={actionStatusData} />}
                />
            </div>

            {/* Dynamic Recent Activity */}
            <div className="p-6 w-full bg-white rounded-lg shadow-md">
                <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
                <div className="space-y-4">
                    {activity.recentObservations.map((obs, idx) => (
                        <div
                            key={`obs-${idx}`}
                            // Adjusted border color based on observation type
                            className={`border-l-4 ${obs.type === 'unsafe act' ? 'border-amber-500' : 'border-red-500'} pl-4 py-2`}
                        >
                            <p className="font-medium">
                                {/* Display the correct observation type */}
                                New {obs.type === 'unsafe act' ? 'Unsafe Act' : 'Unsafe Condition'} observation
                            </p>
                            <p className="text-sm text-gray-500">
                                Area: {obs.areaName} • {formatTimeAgo(obs.createdAt)}
                            </p>
                        </div>
                    ))}

                    {activity.recentUsers.map((user, idx) => (
                        <div
                            key={`user-${idx}`}
                            className="border-l-4 border-purple-500 pl-4 py-2" // Changed border color for users
                        >
                            <p className="font-medium">New user joined</p>
                            <p className="text-sm text-gray-500">
                                {user.name} ({user.accountType}) •{' '}
                                {formatTimeAgo(user.createdAt)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ icon, label, value, bg }) => (
    <div className={`card bg-white flex items-center`}>
        <div className={`rounded-full ${bg} p-3 mr-4`}>{icon}</div>
        <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-2xl font-semibold">{value}</p>
        </div>
    </div>
);

const ChartCard = ({ title, chart }) => (
    <div className="p-6 bg-white rounded-lg shadow-md bg-white">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        <div className="h-64 flex flex-col justify-center items-center">
            {chart}
        </div>
    </div>
);

export default AdminDashboard;
